import React, { useState, Component } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { HomeIcon, BuildingIcon, CalendarIcon, UsersIcon, TagIcon, BarChart3Icon, LogOutIcon, MenuIcon, XIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminPropertyList from '../components/admin/AdminPropertyList';
import AdminBookingList from '../components/admin/AdminBookingList';
import AdminUserList from '../components/admin/AdminUserList';
import AdminPropertyForm from '../components/admin/AdminPropertyForm';
const AdminDashboardPage = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  // Redirect to login if not authenticated
  if (!user) {
    navigate('/auth');
    return null;
  }
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const handleEditProperty = property => {
    setSelectedProperty(property);
    setIsAddingProperty(false);
    setActiveSection('editProperty');
  };
  const handleAddNewProperty = () => {
    setSelectedProperty(null);
    setIsAddingProperty(true);
    setActiveSection('addProperty');
  };
  const handlePropertySaved = () => {
    setActiveSection('properties');
    setSelectedProperty(null);
    setIsAddingProperty(false);
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <AdminDashboardOverview data-id="element-630" />;
      case 'properties':
        return <AdminPropertyList onEditProperty={handleEditProperty} onAddNewProperty={handleAddNewProperty} data-id="element-631" />;
      case 'editProperty':
      case 'addProperty':
        return <AdminPropertyForm property={selectedProperty} onSaved={handlePropertySaved} data-id="element-632" />;
      case 'bookings':
        return <AdminBookingList data-id="element-633" />;
      case 'users':
        return <AdminUserList data-id="element-634" />;
      case 'categories':
        return <AdminCategoriesSection data-id="element-635" />;
      default:
        return <AdminDashboardOverview data-id="element-636" />;
    }
  };
  return <div className="min-h-screen bg-[#F8F5F0] flex flex-col" data-id="element-637">
      {/* Admin Header */}
      <header className="bg-[#006D77] text-white shadow-md sticky top-0 z-50" data-id="element-638">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center" data-id="element-639">
          <Link to="/admin" className="flex items-center space-x-2" data-id="element-640">
            <HomeIcon className="h-8 w-8 text-[#D4A017]" data-id="element-641" />
            <span className="font-bold text-xl md:text-2xl" data-id="element-642">
              Minayo <span className="text-sm font-normal" data-id="element-643">Admin</span>
            </span>
          </Link>
          <div className="flex items-center space-x-4" data-id="element-644">
            <div className="hidden md:flex items-center space-x-2" data-id="element-645">
              <span data-id="element-646">{user.name}</span>
              <div className="bg-[#D4A017] rounded-full w-8 h-8 flex items-center justify-center text-white font-semibold" data-id="element-647">
                {user.name.charAt(0)}
              </div>
            </div>
            <button className="md:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-648">
              {isMobileMenuOpen ? <XIcon className="h-6 w-6 text-white" data-id="element-649" /> : <MenuIcon className="h-6 w-6 text-white" data-id="element-650" />}
            </button>
          </div>
        </div>
      </header>

      <div className="flex-grow flex flex-col md:flex-row" data-id="element-651">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:block w-64 bg-white shadow-md" data-id="element-652">
          <div className="p-4 sticky top-20" data-id="element-653">
            <nav className="space-y-1" data-id="element-654">
              <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'dashboard' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => setActiveSection('dashboard')} data-id="element-655">
                <BarChart3Icon className="h-5 w-5 mr-3" data-id="element-656" />
                <span data-id="element-657">Dashboard</span>
              </button>
              <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'properties' || activeSection === 'editProperty' || activeSection === 'addProperty' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => setActiveSection('properties')} data-id="element-658">
                <BuildingIcon className="h-5 w-5 mr-3" data-id="element-659" />
                <span data-id="element-660">Properties</span>
              </button>
              <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'bookings' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => setActiveSection('bookings')} data-id="element-661">
                <CalendarIcon className="h-5 w-5 mr-3" data-id="element-662" />
                <span data-id="element-663">Bookings</span>
              </button>
              <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'users' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => setActiveSection('users')} data-id="element-664">
                <UsersIcon className="h-5 w-5 mr-3" data-id="element-665" />
                <span data-id="element-666">Users</span>
              </button>
              <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'categories' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => setActiveSection('categories')} data-id="element-667">
                <TagIcon className="h-5 w-5 mr-3" data-id="element-668" />
                <span data-id="element-669">Categories</span>
              </button>
              <hr className="my-4 border-gray-200" data-id="element-670" />
              <button className="flex items-center w-full p-3 rounded-md text-red-600 hover:bg-red-50" onClick={handleLogout} data-id="element-671">
                <LogOutIcon className="h-5 w-5 mr-3" data-id="element-672" />
                <span data-id="element-673">Logout</span>
              </button>
            </nav>
          </div>
        </aside>

        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50" data-id="element-674">
            <div className="bg-white w-3/4 h-full overflow-y-auto p-4" data-id="element-675">
              <div className="flex items-center mb-6" data-id="element-676">
                <div className="bg-[#D4A017] rounded-full w-10 h-10 flex items-center justify-center text-white font-semibold" data-id="element-677">
                  {user.name.charAt(0)}
                </div>
                <div className="ml-3" data-id="element-678">
                  <p className="font-medium" data-id="element-679">{user.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-680">Administrator</p>
                </div>
              </div>
              <nav className="space-y-1" data-id="element-681">
                <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'dashboard' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
              setActiveSection('dashboard');
              setIsMobileMenuOpen(false);
            }} data-id="element-682">
                  <BarChart3Icon className="h-5 w-5 mr-3" data-id="element-683" />
                  <span data-id="element-684">Dashboard</span>
                </button>
                <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'properties' || activeSection === 'editProperty' || activeSection === 'addProperty' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
              setActiveSection('properties');
              setIsMobileMenuOpen(false);
            }} data-id="element-685">
                  <BuildingIcon className="h-5 w-5 mr-3" data-id="element-686" />
                  <span data-id="element-687">Properties</span>
                </button>
                <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'bookings' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
              setActiveSection('bookings');
              setIsMobileMenuOpen(false);
            }} data-id="element-688">
                  <CalendarIcon className="h-5 w-5 mr-3" data-id="element-689" />
                  <span data-id="element-690">Bookings</span>
                </button>
                <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'users' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
              setActiveSection('users');
              setIsMobileMenuOpen(false);
            }} data-id="element-691">
                  <UsersIcon className="h-5 w-5 mr-3" data-id="element-692" />
                  <span data-id="element-693">Users</span>
                </button>
                <button className={`flex items-center w-full p-3 rounded-md ${activeSection === 'categories' ? 'bg-[#006D77] text-white' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
              setActiveSection('categories');
              setIsMobileMenuOpen(false);
            }} data-id="element-694">
                  <TagIcon className="h-5 w-5 mr-3" data-id="element-695" />
                  <span data-id="element-696">Categories</span>
                </button>
                <hr className="my-4 border-gray-200" data-id="element-697" />
                <button className="flex items-center w-full p-3 rounded-md text-red-600 hover:bg-red-50" onClick={handleLogout} data-id="element-698">
                  <LogOutIcon className="h-5 w-5 mr-3" data-id="element-699" />
                  <span data-id="element-700">Logout</span>
                </button>
              </nav>
            </div>
          </div>}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8" data-id="element-701">{renderContent()}</main>
      </div>
    </div>;
};
// Dashboard Overview Component
const AdminDashboardOverview = () => {
  // Dummy data for dashboard
  const revenueData = {
    total: 42580,
    increase: 12.5,
    chartData: [4200, 5100, 5800, 8000, 7500, 12000]
  };
  const bookingsData = {
    total: 156,
    increase: 8.2,
    chartData: [15, 22, 18, 25, 30, 46]
  };
  const propertyData = {
    total: 8,
    bnb: 5,
    farmstay: 3
  };
  const recentBookings = [{
    id: 'B1245',
    guest: 'Emma Wilson',
    property: 'Charming Beachside B&B',
    date: '15 Oct - 20 Oct',
    amount: '$1,250',
    status: 'confirmed'
  }, {
    id: 'B1244',
    guest: 'Michael Johnson',
    property: 'Mountain View Farmstay',
    date: '05 Nov - 10 Nov',
    amount: '$975',
    status: 'pending'
  }, {
    id: 'B1243',
    guest: 'Sophia Martinez',
    property: 'Art Deco B&B',
    date: '10 Jul - 15 Jul',
    amount: '$1,600',
    status: 'confirmed'
  }];
  return <div data-id="element-702">
      <h1 className="text-2xl md:text-3xl font-bold text-[#333333] mb-6" data-id="element-703">
        Dashboard Overview
      </h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-704">
        {/* Revenue Card */}
        <div className="bg-white rounded-lg shadow-md p-6" data-id="element-705">
          <div className="flex justify-between items-start mb-4" data-id="element-706">
            <div data-id="element-707">
              <h3 className="text-gray-500 text-sm" data-id="element-708">Total Revenue</h3>
              <p className="text-2xl font-bold" data-id="element-709">${revenueData.total}</p>
            </div>
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full" data-id="element-710">
              +{revenueData.increase}%
            </span>
          </div>
          <div className="h-20 flex items-end space-x-2" data-id="element-711">
            {revenueData.chartData.map((value, index) => <div key={index} className="bg-[#006D77] rounded-t w-8 md:w-10" style={{
            height: `${value / Math.max(...revenueData.chartData) * 100}%`
          }} data-id="element-712"></div>)}
          </div>
        </div>

        {/* Bookings Card */}
        <div className="bg-white rounded-lg shadow-md p-6" data-id="element-713">
          <div className="flex justify-between items-start mb-4" data-id="element-714">
            <div data-id="element-715">
              <h3 className="text-gray-500 text-sm" data-id="element-716">Total Bookings</h3>
              <p className="text-2xl font-bold" data-id="element-717">{bookingsData.total}</p>
            </div>
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full" data-id="element-718">
              +{bookingsData.increase}%
            </span>
          </div>
          <div className="h-20 flex items-end space-x-2" data-id="element-719">
            {bookingsData.chartData.map((value, index) => <div key={index} className="bg-[#D4A017] rounded-t w-8 md:w-10" style={{
            height: `${value / Math.max(...bookingsData.chartData) * 100}%`
          }} data-id="element-720"></div>)}
          </div>
        </div>

        {/* Properties Card */}
        <div className="bg-white rounded-lg shadow-md p-6" data-id="element-721">
          <div className="mb-4" data-id="element-722">
            <h3 className="text-gray-500 text-sm" data-id="element-723">Property Distribution</h3>
            <p className="text-2xl font-bold" data-id="element-724">
              {propertyData.total} Properties
            </p>
          </div>
          <div className="flex items-center space-x-4" data-id="element-725">
            <div className="flex-1" data-id="element-726">
              <div className="flex justify-between text-sm mb-1" data-id="element-727">
                <span data-id="element-728">B&B</span>
                <span data-id="element-729">{propertyData.bnb}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-730">
                <div className="bg-blue-500 h-2 rounded-full" style={{
                width: `${propertyData.bnb / propertyData.total * 100}%`
              }} data-id="element-731"></div>
              </div>
            </div>
            <div className="flex-1" data-id="element-732">
              <div className="flex justify-between text-sm mb-1" data-id="element-733">
                <span data-id="element-734">Farmstay</span>
                <span data-id="element-735">{propertyData.farmstay}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-736">
                <div className="bg-green-500 h-2 rounded-full" style={{
                width: `${propertyData.farmstay / propertyData.total * 100}%`
              }} data-id="element-737"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8" data-id="element-738">
        <h2 className="text-xl font-semibold mb-4" data-id="element-739">Recent Bookings</h2>
        <div className="overflow-x-auto" data-id="element-740">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-741">
            <thead data-id="element-742">
              <tr data-id="element-743">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-744">
                  Booking ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-745">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-746">
                  Property
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-747">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-748">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-749">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-750">
              {recentBookings.map(booking => <tr key={booking.id} className="hover:bg-gray-50" data-id="element-751">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-752">
                    {booking.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-753">
                    {booking.guest}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-754">
                    {booking.property}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-755">
                    {booking.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-756">
                    {booking.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-757">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-758">
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-right" data-id="element-759">
          <button className="text-[#006D77] hover:underline font-medium text-sm" data-id="element-760">
            View All Bookings
          </button>
        </div>
      </div>
    </div>;
};
// Categories Section Component
const AdminCategoriesSection = () => {
  const categories = [{
    id: 1,
    name: 'Bed & Breakfast',
    slug: 'bnb',
    count: 5
  }, {
    id: 2,
    name: 'Farmstay',
    slug: 'farmstay',
    count: 3
  }];
  return <div data-id="element-761">
      <h1 className="text-2xl md:text-3xl font-bold text-[#333333] mb-6" data-id="element-762">
        Categories
      </h1>
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-763">
        <div className="flex justify-between items-center mb-6" data-id="element-764">
          <h2 className="text-xl font-semibold" data-id="element-765">Property Types</h2>
          <button className="bg-[#006D77] hover:bg-[#005a63] text-white px-4 py-2 rounded-md text-sm font-medium" data-id="element-766">
            Add Category
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-767">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-768">
            <thead className="bg-gray-50" data-id="element-769">
              <tr data-id="element-770">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-771">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-772">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-773">
                  Properties
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-774">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-775">
              {categories.map(category => <tr key={category.id} className="hover:bg-gray-50" data-id="element-776">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-777">
                    {category.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-778">
                    {category.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-779">
                    {category.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-780">
                    <button className="text-blue-500 hover:text-blue-700 mr-3" data-id="element-781">
                      Edit
                    </button>
                    <button className="text-red-500 hover:text-red-700" data-id="element-782">
                      Delete
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
export default AdminDashboardPage;